import { NgIf, CommonModule, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, SimpleChange, ViewEncapsulation, forwardRef, inject } from '@angular/core';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatDrawerToggleResult } from '@angular/material/sidenav';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FileManagerService } from 'app/modules/admin/apps/file-manager/file-manager.service';
import { Item, ItemLabel } from 'app/modules/admin/apps/file-manager/file-manager.types';
import { FileManagerListComponent } from 'app/modules/admin/apps/file-manager/list/list.component';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { FileManagerComponent } from 'app/modules/admin/apps/file-manager/file-manager.component';
import { ActivitiesComponent } from '../activities/activities.component';
import { NotesComponent } from '../notes/notes.component';
import { PermissionsComponent } from '../permissions/permissions.component';
import { FuseFindByKeyPipe } from '@fuse/pipes/find-by-key/find-by-key.pipe';
import { labelColorDefs } from 'app/shared/global-constants';
import { DocumentService } from 'app/services/document.service';
import { Asset } from 'app/models/document';
import { NgxLoadingModule } from 'ngx-loading';
import { LabelService } from 'app/services/label.service';
import { Label } from 'app/models/label';
import { Util } from 'app/models/util';
import { ToastrService } from 'ngx-toastr';
import { FavouriteService } from 'app/services/favourite.service';
import { EventService } from 'app/services/event.service';
import { FileSizePipe } from '@fuse/pipes/file-size.pipe';
import { FullNamePipe } from '@fuse/pipes/full-name.pipe';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthService } from 'app/services/auth.service';
import { ActionDirective } from '@fuse/directives/action.directive';
import { MatButtonVariantDirective } from '@fuse/directives/mat-button-variant.directive';
import { IsDisplayedDirective } from '@fuse/directives/isDisplayed.directive';
import { LpmLabelPipe } from '@fuse/pipes/lpm-label.pipe';
import { DesktopAgentService } from 'app/services/desktop-agent.service';

@Component({
    selector: 'file-manager-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.scss'],
    // encapsulation: ViewEncapsulation.None,
    // changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatCheckboxModule,
        MatRippleModule,
        RouterLink,
        MatInputModule,
        MatMenuModule,
        MatIconModule,
        NgIf,
        NgClass,
        MatTabsModule,
        ActivitiesComponent,
        NotesComponent,
        PermissionsComponent,
        NgClass,
        NgxLoadingModule,
        FileSizePipe,
        FullNamePipe,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        ActionDirective,
        forwardRef(() => IsDisplayedDirective),
        MatButtonVariantDirective,
        forwardRef(() => NotesComponent),
        LpmLabelPipe,

    ],
})

export class FileManagerDetailsComponent implements OnInit, OnDestroy {

    @Input() docId = null;
    @Input()  selectedTab = 0;
    configForm: UntypedFormGroup;
    isLoading: boolean = false;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    // Get the label colors
    labelColors = labelColorDefs;

    labels: Label[] = [];

    // Default to the first tab
    asset: Asset = new Asset();
    public modalRef;
    public selectedAsset: Asset = null;
    subs = new Subscription();
    rootFolderId = null;
    isWebComponent = false;

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _fileManagerListComponent: FileManagerListComponent,
        public _fileManagerService: FileManagerService,
        private _formBuilder: UntypedFormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        public _fileManagerComponent: FileManagerComponent,
        private _router: Router,
        private route: ActivatedRoute,
        private docService: DocumentService,
        private favService: FavouriteService,
        private labelService: LabelService,
        public util: Util,
        private toastr: ToastrService,
        private eventSer: EventService,
        private _matDialog: MatDialog,
        public sanitizer: DomSanitizer,
        public authService: AuthService,
        private labelPipe: LpmLabelPipe,
        public desktopAgentService: DesktopAgentService,
    ) {
        this.isWebComponent = this.util.getIsWebApp();
        this.rootFolderId = this.util.getRootFromSession();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
		for (let propName in changes) {
			let changedProp = changes[propName];
			let to = changedProp.currentValue; //JSON.stringify();
			if (changedProp.isFirstChange()) {
			} else {
				let from = JSON.stringify(changedProp.previousValue);
				if (`${propName}` == `docId`) {
                    this.docId = to;
                    if (this.docId && this.util.getIsWebApp()) this.getDoc();
				} 
			}
		}
	}

    /**
     * On init
     */
    ngOnInit(): void {
        //Open the drawer
        this.route.paramMap.subscribe((param) => {
            if(param.get("id") && !this.util.getIsWebApp()){
                this.docId = param.get("id");
                this.getDoc();
            }
        });

        this.route.queryParams.subscribe(params => {
            this.selectedTab = 0;
            if (params['activeTab'] && !this.util.getIsWebApp())  {
                this.selectedTab = +params['activeTab']; // '+' converts the string to a number
            }
        });

        this.subs.add(
            this.eventSer.getData.subscribe((data) => {
                if(this.docId)
                this.getDoc()
            })
        );

        this.subs.add(
            this.eventSer.multiRowSelect.subscribe(() => {
                    if (this.util.getIsWebApp()) {
                        this.closeDrawer();
                    } else {
                        this._router.navigate(['../../'], { relativeTo: this.route }); 
                    }
            })
        );

        if(!this.util.getIsWebApp()) this.openClosePanel();
        // Build the config form
        this.configForm = this._formBuilder.group(
            this.util.trashForm
        );
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
       this.subs.unsubscribe();
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    openClosePanel(){
        // Get the item
        this._fileManagerService.item$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((item: Item) => {
                // Open the drawer in case it is closed
                this._fileManagerListComponent.matDrawer.open();

                // Get the item
                //this.item = item;
                console.log('item', item);

                // Mark for check
                this._changeDetectorRef.markForCheck();

    })
}

    /**
     * Retrieves the document by its ID and assigns the data to the `asset` property.
     * Sets the `isLoading` flag to true during the retrieval process and false when done.
     * Logs any errors that occur during the retrieval process.
     */
    getDoc() {
        this.getLabels();
        this.isLoading = true;
        this.docService.getDocById(this.docId).then((res) => {
            this.asset = this.docService.getData(res);
            this.isLoading = false;
            if(this.util.getIsWebApp())
            this.openClosePanel();

        }).catch((error) => {
            this.isLoading = false;
            console.log(error);
        })
    }

    /**
     * Retrieves the labels from the label service.
     * Sets the `isLoading` flag to true before making the API call.
     * Updates the `labels` property with the data returned from the API call.
     * Sets the `isLoading` flag to false after the API call is completed.
     * Logs any errors that occur during the API call.
     */
    getLabels() {
        this.isLoading = true;
        this.labelService.getLabels().then((res) => {
            this.labels = this.labelService.getData(res);
            this.isLoading = false;

        }).catch((error) => {
            this.isLoading = false;
            console.log(error);
        })
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Close the drawer
     */
    closeDrawer(): Promise<MatDrawerToggleResult> {
        if(this.util.getIsWebApp())  this._fileManagerListComponent.drawerView(null, null);
        return this._fileManagerListComponent.matDrawer.close();
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    /**
    * Open confirmation dialog
    * Delete the item after confirmation
    */
    openDialog_delete(assetId, type): void {
        const dialogRef = this._fuseConfirmationService.open(this.configForm.value);

        // Subscribe to afterClosed from the dialog reference
        dialogRef.afterClosed().subscribe((result) => {
            //console.log(result);
            if (result === 'confirmed') {
                this.trash(assetId, type);
            }
        });
    }

    /**
     * Moves an asset to the trash.
     * @param {string} assetId - The ID of the asset to be moved to the trash.
     * @param {string} type - The type of the asset ('file' or 'folder').
     */
    trash(assetId:string, type:'file' | 'folder'): void {
        const ids = [assetId]
        this.docService.trash(ids).then((res) => {
            this.toastr.warning(`${type === 'file' ? 'File' : 'Folder'} moved to ${this.labelPipe.transform('Trash', 'trashLocationLabel')}`, "Trashed");
            const parentId = this.asset.parent ? this.asset.parent.id : null;
           
            if (this.util.getIsWebApp()) {
                this.eventSer.onRouteChange.emit(parentId);
                this.closeDrawer();
                return;
            }
            if (parentId == this.rootFolderId) {
                this._router.navigate(['/apps/file-manager']);
            }

            this._router.navigate(['/apps/file-manager/folders', parentId]);
            this.eventSer.getData.emit(true);

            this.closeDrawer();

        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Toggle star
     */
    toggleStar(asset): void {
        asset.favourite = asset.favourite ? !asset.favourite : true;
        this.favService.favorite(asset.id, asset.favourite).then((res) => {
            if (!asset.favourite) {
                this.toastr.warning("Removed from favorite")
            } else {
                this.toastr.info("Added to favorite")
            }
            this._fileManagerListComponent.getAssets();
            this.eventSer.checkUnreadMessages.emit("favourite");
        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Toggle label
     *
     * @param label
     */
    toggleLabel(assetId, labelId, event: MatCheckbox): void {
        if (!event.checked) {
            this.labelService.assignLabel(labelId, assetId).then((res) => {
                this.toastr.info("Label applied");
                this.eventSer.checkUnreadMessages.emit("Label applied");
                this.getDoc();
                this.eventSer.getData.emit(true);
            }).catch((error) => {
                console.log(error);
            })
        } else this.deleteLabel(labelId, assetId);
    }

    /**
     * Checks if the given ID exists in the provided list.
     * @param id - The ID to check for.
     * @param list - The list to search in.
     * @returns `true` if the ID exists in the list, `false` otherwise.
     */
    selectedLabel(id, list: any[]) {
        if (list) {
            const index = list.findIndex(item => item.id === id)
            if (index == -1)
                return false
            else
                return true
        }
    }

    /**
     * Deletes a label from an asset.
     * @param {number} labelId - The ID of the label to be removed.
     * @param {number} assetId - The ID of the asset from which the label should be removed.
     * @returns {Promise<void>} - A promise that resolves when the label is successfully removed.
     */
    deleteLabel(labelId, assetId) {
        this.labelService.removeLabel(labelId, assetId).then((res) => {
            this.toastr.warning("Label removed");
            this.eventSer.checkUnreadMessages.emit("Label removed");
            this.getDoc();
            this.eventSer.getData.emit(true);
        }).catch((error) => {
            console.log(error);
        })
    }

    /**
     * Checks if a value exists in an array.
     * 
     * @param array - The array to check.
     * @param value - The value to search for.
     * @returns `true` if the value is found in the array, `false` otherwise.
     */
    checkValueInArray(array, value) {
        return array.includes(value);
    }

    goToParent(folderId: string) {
        if (window.location.pathname == `/apps/file-manager/details/${this.docId}`)
            this._router.navigate(['/apps/file-manager']);
        else
            this._router.navigate(['/apps/file-manager/folders/' + folderId]);
    }

    public desModal(modal, asset) {
        this.selectedAsset = { ...asset };
        this.modalRef = this._matDialog.open(modal);
    }

    updateDescription(): void {
        this.isLoading = true;
        this.docService.saveFolder(this.selectedAsset).then((res) => {
            this.isLoading = false;
            this.cancel();
            this.getDoc();
            this.toastr.success("Item description update successfully");
        }).catch((error) => {
            this.isLoading = false;
            console.log(error);
        })
    }

    cancel() {
        this.modalRef.close()
    }

    onThumbnailClick(asset: Asset) {
       this.util.onThumbnailClick(asset);
    }

    /**
     * Opens the file in the DMS Desktop Agent using the drive:// custom protocol.
     * @param asset - The file asset to open.
     */
    openOnDesktop(asset: Asset): void {
        this.desktopAgentService.launch(asset);
    }

}
